import { useEffect, useState, useRef, useCallback } from 'react';
import { Box, BoxProps, Timeout } from 'grommet';
import styled from 'styled-components/macro';
import { GqlErrorContextProvider, PendingChangesInfoNoDrop, base, ColorSpec } from '@sdxcorp/sdx-dds-gui-lib';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { adminRouteTo } from '../../../routing';
import { colorValue, defaultTransition } from '../../../../lib/theme';
import SDXLogo from '../../../../base/components/atoms/SDXLogo';
import UserInfo from '../../../../base/components/molecules/UserInfo/UserInfo';
import { useAuth } from '../../../../base/auth/useAuth';
import { AdminAuthContext } from '../../../auth/authContext';
import { SchedulerStatusesHeader, CalendarDatesHeader } from '@client/admin/state/types';
import ProcessingDate from '../../../../base/components/molecules/ProcessingDate';
import EnvType from './EnvType';
import { AdminAbilities } from '@client/admin/auth/abilities';

const {
  atoms: {
    Icons: { Hamburger },
  },
  molecules: {
    StatusContainer: { default: StatusContainer },
  },
} = base;

const POLLING_TIMEOUT_SHORT_MS = 5000;
const POLLING_TIMEOUT_LONG_MS = 60 * 1000;

enum SEGMENT_STATUS {
  OPEN = 'OPEN',
  CLOSED = 'CLOSED',
  IDLE = 'IDLE',
}

const statusColors: Record<string, { bg: ColorSpec; border: ColorSpec; text: string }> = {
  [SEGMENT_STATUS.OPEN]: { bg: 'con-success', border: 'con-success-light', text: '#90999A' },
  [SEGMENT_STATUS.CLOSED]: { bg: 'con-error', border: 'con-error-light', text: '#90999A' },
  [SEGMENT_STATUS.IDLE]: { bg: 'con-disabled', border: 'con-disabled', text: '#90999A' },
};

interface Props extends Pick<BoxProps, 'gridArea'> {
  onMenuClick?: () => void;
}

const StyledMenu = styled(Hamburger)`
  margin-left: -12px;
  padding: 8px 12px;
  border-radius: 3px;
  transition: ${defaultTransition('background-color')};
  cursor: pointer;

  &:hover {
    background-color: ${props => colorValue(props.theme, c => c['grey-10'])};
  }
  &:active {
    background-color: ${props => colorValue(props.theme, c => c['grey-20'])};
  }

  height: 20px;
  width: 20px;
  cursor: pointer;

  color: ${({ theme }) => colorValue(theme, c => c['grey-80'])};
`;

const StyledBox = styled(Box)`
  background-color: ${({ theme }) => colorValue(theme, c => c.white)};
  z-index: 2;
  -ms-grid-column: 1;
  grid-column: 1;
  -ms-grid-row: 1;
  grid-row: 1;
`;

const StyledSeparator = styled(Box)`
  background-color: ${props => props.theme.global.colors.white};
  opacity: 0.1;
  width: 1px;
  height: 32px;
  width: 8px;
`;

const LeftSlot = styled.div`
  position: relative;
  width: 320px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  & a > svg {
    margin-left: 0;
  }
`;

const HamburgerAnchor = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
`;

export const twoCalendarDatesQuery = (date: string | undefined, currCalCodeFirst: string, currCalCodeSecond: string) => gql`
  query CalendarDatesHeader {
    firstDate: businessCalendarDays(query: "[[?e :date ?date][(= ?date $1)][?e :calendar ?c][?c :name $2]]", args: ["${date}", "${currCalCodeFirst}"]) {
      isBusinessDay
    }
    secondDate: businessCalendarDays(query: "[[?e :date ?date][(= ?date $1)][?e :calendar ?c][?c :name $2]]", args: ["${date}", "${currCalCodeSecond}"]) {
      isBusinessDay
    }
}
`;

export const schedulerStatusesQuery = gql`
  query SchedulerStatusesHeader {
    schedulerStatuses {
      segmentStatuses
      processingDate
    }
  }
`;

const Header = (props: Props) => {
  const { t } = useTranslation();
  const calendarDatesTimeout = useRef<Timeout>();
  const [processingDate, setProcessingDate] = useState<string | undefined>();
  const [calendarDatesData, setCalendarDatesData] = useState<CalendarDatesHeader>();
  const [isNotOnBehalfOfSnb, setIsNotOnBehalfOfSnb] = useState<boolean>(true);

  const {
    data: schedulerStatusesData,
    loading: schedulerStatusesLoading,
    startPolling: startPollingSchedulerStatuses,
    stopPolling: stopPollingSchedulerStatuses,
    client: apolloClient,
  } = useQuery<SchedulerStatusesHeader>(schedulerStatusesQuery, {
    fetchPolicy: 'network-only',
    onError: error => {
      setIsNotOnBehalfOfSnb(false);
    },
  });

  const getTwoCalendarDates = useCallback(() => {
    apolloClient
      .query<CalendarDatesHeader>({
        query: twoCalendarDatesQuery(processingDate, 'CHF', 'EUR'),
        fetchPolicy: 'network-only',
        errorPolicy: 'ignore',
      })
      .then(result => {
        setCalendarDatesData(result.data);
      });
    calendarDatesTimeout.current = setTimeout(getTwoCalendarDates, POLLING_TIMEOUT_SHORT_MS) as unknown as Timeout;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingDate]);

  useEffect(() => {
    if (processingDate) {
      getTwoCalendarDates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processingDate]);

  useEffect(() => {
    setProcessingDate(schedulerStatusesData?.schedulerStatuses?.[0]?.processingDate);
  }, [schedulerStatusesData]);

  // twoCalendarDatesQuery returns two values for chf and eur curr., for currency nonBusiness Day  returns false otherwise undefined
  const isChfBusinessDay = calendarDatesData?.firstDate?.[0]?.isBusinessDay === undefined ? true : calendarDatesData?.firstDate?.[0]?.isBusinessDay;
  const isEurBusinessDay = calendarDatesData?.secondDate?.[0]?.isBusinessDay === undefined ? true : calendarDatesData?.secondDate?.[0]?.isBusinessDay;

  const segmentStatuses = schedulerStatusesData?.schedulerStatuses?.[0]?.segmentStatuses ?? {};
  const { SETTLEMENT, CASH_OUT_SETTLEMENT, CASH_OUT_EUR_SETTLEMENT } = segmentStatuses;
  const chfOpen = CASH_OUT_SETTLEMENT === SEGMENT_STATUS.OPEN && isChfBusinessDay;
  const eurOpen = CASH_OUT_EUR_SETTLEMENT === SEGMENT_STATUS.OPEN && isEurBusinessDay;

  const { onMenuClick } = props;
  const { loading, user, canISilent } = useAuth<AdminAbilities>(AdminAuthContext);
  const showUserRelatedButtons = !loading && !!user;
  const chfStatusLabel = chfOpen ? 'header.status.payments.chfOpen' : 'header.status.payments.chfClosed';
  const eurStatusLabel = eurOpen ? 'header.status.payments.eurOpen' : 'header.status.payments.eurClosed';

  const calendarDatesLoading = calendarDatesData === undefined;

  const chfStatusColor = calendarDatesLoading ? SEGMENT_STATUS.IDLE : chfOpen ? SEGMENT_STATUS.OPEN : SEGMENT_STATUS.CLOSED;
  const eurStatusColor = calendarDatesLoading ? SEGMENT_STATUS.IDLE : eurOpen ? SEGMENT_STATUS.OPEN : SEGMENT_STATUS.CLOSED;

  const visibilityChangeHandler = useCallback(() => {
    if (document.hidden) {
      stopPollingSchedulerStatuses();
      clearTimeout(calendarDatesTimeout.current);
      startPollingSchedulerStatuses(POLLING_TIMEOUT_LONG_MS);
      calendarDatesTimeout.current = setTimeout(getTwoCalendarDates, POLLING_TIMEOUT_LONG_MS) as unknown as Timeout;
    } else {
      startPollingSchedulerStatuses(POLLING_TIMEOUT_SHORT_MS);
      clearTimeout(calendarDatesTimeout.current);
      calendarDatesTimeout.current = setTimeout(getTwoCalendarDates, POLLING_TIMEOUT_SHORT_MS) as unknown as Timeout;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTwoCalendarDates]);

  useEffect(() => {
    startPollingSchedulerStatuses(POLLING_TIMEOUT_SHORT_MS);

    // https://developer.mozilla.org/en-US/docs/Web/API/Document/hidden
    document.addEventListener('visibilitychange', visibilityChangeHandler);

    return () => {
      stopPollingSchedulerStatuses();
      clearTimeout(calendarDatesTimeout.current);
      document.removeEventListener('visibilitychange', visibilityChangeHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibilityChangeHandler]);

  // TODO: Do not use GqlErrorContextProvider --> library should fully implement this logic
  // onLogoClick={() => navigateTo(adminRouteTo.dashboard())}
  return (
    <GqlErrorContextProvider>
      <StyledBox height="64px" pad={{ right: 'medium' }} flex direction="row" align="center" elevation="xsmall">
        <LeftSlot>
          <HamburgerAnchor>
            <StyledMenu id="menu" onClick={onMenuClick} />
          </HamburgerAnchor>
          <SDXLogo url={adminRouteTo.dashboard()} openInNewPage={false} />
        </LeftSlot>
        <Box flex="grow">&nbsp;</Box>
        <EnvType />
        <Box margin={{ left: '10px' }}>
          <ProcessingDate shouldRefetchOnPathnameChange={true} showCalendarIcon={true} />
        </Box>
        <StyledSeparator />
        <Box>
          {isNotOnBehalfOfSnb && (
            <StatusContainer
              testId="settlement-status"
              status={t(SETTLEMENT === SEGMENT_STATUS.OPEN ? 'header.status.settlement.open' : 'header.status.settlement.closed')}
              colors={statusColors[schedulerStatusesLoading ? SEGMENT_STATUS.IDLE : SETTLEMENT]}
            />
          )}
        </Box>
        {isNotOnBehalfOfSnb && !canISilent('asset-types-cryptocurrency') && (
          <>
            <StyledSeparator />
            <Box>
              <StatusContainer testId="detokenization-chf" status={t(chfStatusLabel)} colors={statusColors[chfStatusColor]} />
            </Box>
            <StyledSeparator />
            <Box>
              <StatusContainer testId="detokenization-eur" status={t(eurStatusLabel)} colors={statusColors[eurStatusColor]} />
            </Box>
          </>
        )}
        {showUserRelatedButtons && (
          <Box flex="shrink" direction="row" align="center">
            {/* turning off notifications <Notification /> */}
            <StyledSeparator />
            <PendingChangesInfoNoDrop title="4-Eyes" />
            <UserInfo />
          </Box>
        )}
      </StyledBox>
    </GqlErrorContextProvider>
  );
};

export default Header;
